import Theme from "/components/Theme";
import Head from "next/head";
import dynamic from "next/dynamic";
import Hero from "../../components/organisms/integrations/Hero";
import AppStorage from "../../components/AppStorage";
import TpWidget from "@components/atoms/document/TpWidget";
const LogoAsideWithContent = dynamic(() =>
  import("../../components/organisms/integrations/LogoAsideWithContent")
);
const BoxShadowCard = dynamic(() =>
  import("../../components/organisms/common/BoxShadowCard")
);
const HighlightFeaturesCards = dynamic(() =>
  import("../../components/organisms/common/HighlightFeaturesCards")
);
const Schema = dynamic(() =>
  import("../../components/organisms/common/Schema")
);
const WideCTA = dynamic(() =>
  import("../../components/organisms/common/WideCTA")
);
const People = dynamic(() =>
  import("../../components/organisms/common/People")
);
const CTA = dynamic(() => import("../../components/organisms/common/CTA"));
const FAQ = dynamic(() => import("../../components/organisms/common/FAQ"));
const VideoSchema = dynamic(() =>
  import("../../components/organisms/common/VideoSchema")
);
import {
  viewport,
  colors,
  font,
  lineHeight,
} from "../../styles/cssinjs/helpers/variables.js";

export async function getStaticPaths({ locales }) {
  // Fetch All Path
  const res = await fetch(`${process.env.JSON_PATH}/routes.json`);
  const data = await res.json();

  // Specific Paths
  let routes = data.integrations.all;

  // Variable for returning path
  let paths = [];

  // Create Paths with locales
  routes.forEach((path) => {
    for (const locale of locales) {
      paths.push({
        params: {
          page: path,
        },
        locale,
      });
    }
  });
  // return to getStaticProps()
  return {
    paths,
    fallback: false,
  };
}
export async function getStaticProps({ params, locale }) {
  let allData = {};

  // Fetch
  const headerResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/header.json`
  );
  const footerResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/footer.json`
  );
  const marketingResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/integrations/${params.page}/marketing.json`
  );
  const contentResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/integrations/${params.page}/content.json`
  );

  // if (!marketingResponse.ok) {
  //   console.log("HTTP error! status");
  //   // throw new Error(`HTTP error! status: ${response.status}`);
  //   console.warn(`You may get 404 for /${locale}/${params.page}`);
  //   return {
  //     notFound: true,
  //   };
  // }
  // if (!contentResponse.ok) {
  //   console.warn(`You may get 404 for /${locale}/${params.page}`);
  //   return {
  //     notFound: true,
  //   };
  // }
  // Catch Error
  if (!marketingResponse.ok || !contentResponse.ok) {
    return {
      notFound: true,
    };
  }

  // Convert
  allData.header = await headerResponse.json();
  allData.footer = await footerResponse.json();
  allData.marketing = await marketingResponse.json();
  allData.content = await contentResponse.json();

  // Wrap
  const header = {
    header: allData.header,
  };
  const footer = {
    footer: allData.footer,
  };

  // Combine all data
  const data = await {
    ...header,
    ...footer,
    ...allData.marketing,
    ...allData.content,
  };

  return {
    props: {
      data,
      pageName: params.page,
      lang: locale,
    },
  };
}

export default function Integrations({ data, pageName, lang, nonce }) {
  const { header, footer, seo, content } = data;

  return (
    <>
      <Theme
        header={header}
        footer={footer}
        className={`integration-listing-page ${content.helpers.pagename}`}
        seo={seo}
        nonce={nonce}
      >
        <AppStorage pageName={`Integrations Sub Page - ${pageName}`} />
        <Hero data={content.hero} />
        <section className="bg-offset bg-offset-top bg-light md:pt-2 md:pb-10">
          <BoxShadowCard data={content.integrationFeatures} hasOffset={true} />
        </section>
        <LogoAsideWithContent data={content.howToIntegrate} />
        {content.hasOwnProperty("cta") && (
          <CTA
            data={content.cta}
            imgClassName={`img-fit`}
            className="bg-light lg:pt-20 md:pt-10 cta-section bg-offset-top"
          />
        )}
        {content.hasOwnProperty("highlightFeaturesCards") && (
          <HighlightFeaturesCards
            data={content.highlightFeaturesCards}
            classes="highlight-section"
          />
        )}
        {content.hasOwnProperty("faqs") && (
          <>
            <FAQ
              data={content.faqs}
              className="lg:mb-15 md:mb-10 faq-section bg-offset-top"
            />
            <Schema data={content.faqs.list} />
          </>
        )}
        {!["paypal-integration", "integration-google-reviews"].includes(
          content.helpers.pagename
        ) &&
          ["stripe-integration"].includes(content.helpers.pagename) && (
            <People className={pageName === "videoask" && "bg-light"} />
          )}
        <WideCTA data={content.wideCTA} />
        <TpWidget />
        <style jsx global>{`
          .text-brandcolor-v2 {
            border-bottom: 1px solid ${colors.primitive.black[900]};
          }
          :global(.hero--default) img {
            object-fit: cover;
          }
          .zap-integrations .highlight-section,
          .zap-integrations .cta-section {
            background-color: ${colors.primitive.white.default}!important;
          }
          .zap-integrations .cta-section::before {
            content: "";
            height: 500px;
            background-color: ${colors.primitive.black[200]};
            z-index: 0;
          }
          .zap-integrations .highlight-section,
          .zap-integrations .cta-section {
            background-color: ${colors.primitive.white.default}!important;
          }
          .zap-integrations .cta-section::before {
            content: "";
            height: 500px;
            background-color: ${colors.primitive.black[200]};
            z-index: 0;
          }
          @media (min-width: ${viewport.desktop.sm}) {
            ${
              pageName === "videoask" &&
              `
            div.container.grid--default.has-align-top {
              padding-bottom: 50px;
            }
          `
            }
          }
          .border-bottom{
            border-bottom: 1px solid ${colors.primitive.white.default};
          }
          @media (min-width: ${viewport.tablet.sm}){
           .paypal-integration .hero--default h1{
             font-size: ${font.size.lg[2.5]} !important;
             line-height: ${lineHeight[12]} !important;
           } 
          }
          .paypal-integration .faq-section h2,
          .paypal-integration .highlight-section h2 {
            font-size: ${font.size.lg[2.5]};
            line-height: ${lineHeight[12]};
          }
          .paypal-integration .grid--default .btn-primary {
            width: 240px;
          }
          .paypal-integration .grid--default .logo-content-holder  picture {
            max-width: 100% !important;
          }
          .paypal-integration .bg-offset-top::before,
          .paypal-integration .bg-offset-top::after,
          .paypal-integration .bg-light {
            background-color: ${colors.primitive.grey[600]} !important;
          }
          @media (max-width: ${viewport.tablet.sm}) {
            :global(.hero--default) img {
              height: auto !important;
            }
            .zap-integrations .cta-section::before {
              content: "";
              height: 300px;
            }
            .zap-integrations .highlight-section article {
              padding: 0 !important;
            }
            .zap-integrations .highlight-section article h2 {
              margin-bottom: 0px !important;
              font-size: ${font.size.lg[1]};
              line-height: ${lineHeight[10]};
            }
            .paypal-integration .grid--default .btn-primary {
              width: 100%;
            }
            .paypal-integration .grid--default picture img {
              height: auto;
            }
            .paypal-integration .card--default.card--feature a {
              margin-top: 20px !important;
            }
            .paypal-integration .hero--default h1 ,
            .paypal-integration .faq-section h2,
            .paypal-integration .highlight-section h2 {
              font-size: ${font.size.lg[1]};
              line-height: ${lineHeight[10]};
            }
            .paypal-integration .hero--default h1 {
              margin-bottom:20px !important;
            }
            .paypal-integration .faq-section.bg-offset-top::before {
              height: 160px;
            }
          }
          ${
            pageName === "videoask"
              ? `
              .ordered-bulletin {
                margin-bottom: 24px;
              }`
              : ``
          }
          }
        `}</style>
        {(pageName == "instagram-booking" || pageName == "wordpress") && (
          <Head>
            <VideoSchema data={content.howToIntegrate.content[1][2]} />
          </Head>
        )}
      </Theme>
    </>
  );
}
